import { useEffect, useState, useRef } from "react";
import http from "../Api/http";
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { Navigate } from "react-router-dom";
import { FaBusAlt, FaTruck } from 'react-icons/fa';
import ProgressSpinner from './progressSpinner/innerProgressSpinner';
import { t } from "i18next";

export default function TrafficJams() {
    const { t, i18n } = useTranslation();
    const { user, loadingUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [selectedStep, setSelectedStep] = useState(null);
    const dialogRef = useRef();

    useEffect(() => {
        if (!user) return;
        async function fetchData() {
            setLoading(true);
            const lang = i18n.language || 'ar';
            const res = await http.getJsonAxios(http.actions.Traffic.GetStepsDetails, { lang }, t);
            if (res.status === 200) setData(res.data || []);
            else {
                console.error("Error: " + res.message);
            }
            setLoading(false);
        }
        fetchData();
        const interval = setInterval(fetchData, window.API_URL.interval_time || 30000);
        return () => clearInterval(interval);
    }, [i18n.language, user]);

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (selectedStep && dialogRef.current && !dialogRef.current.contains(e.target)) {
                setSelectedStep(null);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [selectedStep]);

    if (loadingUser) return null;
    if (!user || (user.Role !== 1 && user.Role !== 2)) return <Navigate to="/" />;

    const busSteps = extractFilteredSteps(data, id => (id === 2 || id===3 || id===4));
    const truckSteps = extractFilteredSteps(data, id => id === 1);

    return (
        <div dir={t('dir')} className="min-h-screen-center px-4 py-6 bg-gray-50">
            {loading && <ProgressSpinner loading={loading} />}

            <div className="flex flex-col lg:flex-row gap-4">
                {/* Buses */}
                <div className="lg:w-2/3 order-1"> {/* 🚌 الباصات */}
                    <StepSection
                        icon={<FaBusAlt className="text-blue-500 h-8 w-8" />}
                        title={t("buses")}
                        steps={busSteps}
                        onStepClick={setSelectedStep}
                    />
                </div>

                {/* Trucks */}
                <div className="lg:w-1/3 order-2"> {/* 🚚 الشاحنات */}
                    <StepSection
                        icon={<FaTruck className="text-green-600 h-8 w-8" />}
                        title={t("trucks")}
                        steps={truckSteps}
                        onStepClick={setSelectedStep}
                    />
                </div>
            </div>

            {selectedStep && (
                <StepDetailsModal step={selectedStep} onClose={() => setSelectedStep(null)} dialogRef={dialogRef} t={t} />
            )}
        </div>
    );
}

// Helper: extract steps based on BusTypeId
function extractFilteredSteps(data, typeFilterFn) {
    return data.map(step => {
        const filteredTypes = step.BusTypes.filter(bt => typeFilterFn(bt.BusTypeId));
        return filteredTypes.length > 0 ? { ...step, BusTypes: filteredTypes } : null;
    }).filter(Boolean);
}

// UI: Render grouped steps
function StepSection({ icon, title, steps, onStepClick }) {
    const totalCount = steps.reduce((acc, step) => acc + step.BusTypes.reduce((a, bt) => a + (bt.BusesCount || 0), 0), 0);

    return (
        <div className="flex flex-col border rounded-lg bg-white shadow h-full">
            <div className="p-4 border-b flex flex-col items-center justify-center">
                <h2 className="text-lg font-semibold text-center">{title} ({totalCount})</h2>
                {icon}
            </div>

            <div className="flex-1 overflow-y-auto p-4">
                <div className="flex flex-wrap gap-3 justify-center">
                    {steps.map(step => (
                        <div
                            key={step.StepID}
                            onClick={() => {
                                const allBusDetails = step.BusTypes.flatMap(bt => bt.BusDetails || []);
                                onStepClick({ StepName: step.StepName, BusDetails: allBusDetails });
                            }}
                            className={`rounded-full flex items-center justify-center text-center cursor-pointer font-bold text-white transition-transform hover:scale-105 ${getCircleColor(step.StepID, step.BusTypes[0])} ${getCircleSize(step.BusTypes[0].BusTypeId, steps.length)}`}
                        >
                            <div>
                                {step.StepName}
                                <br />
                                ({step.BusTypes.reduce((sum, bt) => sum + (bt.BusesCount || 0), 0)})
                            </div>
                        </div>
                    ))}

                    {/* {steps.map(step =>
                        step.BusTypes.map(bt => (
                            <div
                                key={`${step.StepID}-${bt.BusTypeId}`}
                                onClick={() => onStepClick({ StepName: step.StepName, BusDetails: bt.BusDetails })}
                                className={`rounded-full flex items-center justify-center text-center cursor-pointer font-bold text-white transition-transform hover:scale-105 ${getCircleColor(step.StepID, bt)} ${getCircleSize(bt.BusTypeId, steps.length)}`}
                            >
                                <div>
                                    {step.StepName}
                                    <br />({bt.BusesCount})
                                </div>
                            </div>
                        ))
                    )} */}
                </div>
            </div>
        </div>
    );
}


// UI: Modal with bus details
function StepDetailsModal({ step, onClose, dialogRef, t }) {

    return (
        <div dir={t('dir')} className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
            <div dir={t('dir')} ref={dialogRef} className="bg-white rounded-xl shadow-lg p-6 max-w-3xl w-full relative">
                <div dir={t('dir')} className="flex justify-between items-center mb-4">
                    <h2 className="text-xl text-center font-bold w-full">📍 {step.StepName}</h2>
                    <button className="absolute top-2 right-4 text-red-600 text-xl" onClick={onClose}><strong>✖</strong></button>
                </div>
                <div className="overflow-x-auto max-h-[500px]" dir={t('dir')}>
                    <table className="min-w-full text-sm border border-gray-300">
                        <thead className="bg-gray-200 text-center">
                            <tr>
                                <th className="px-2 py-1 border">{t("orderNo")}</th>
                                <th className="px-2 py-1 border">{t("busNo")}</th>
                                <th className="px-2 py-1 border">{t("busType")}</th>
                                <th className="px-2 py-1 border">{t("driverName")}</th>
                                <th className="px-2 py-1 border">{t("arrivalTime")}</th>
                                <th className="px-2 py-1 border">{t("duration")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[...step.BusDetails]
                                .sort((a, b) => new Date(a.ArrivalTime) - new Date(b.ArrivalTime))
                                .map((bus, i) => (
                                    <tr key={i} className="hover:bg-gray-50">
                                        <td className="px-2 py-1 border">{i + 1}</td>
                                        <td className="px-2 py-1 border">{bus.BusNo}</td>
                                        <td className="px-2 py-1 border">{bus.BusType}</td>
                                        <td className="px-2 py-1 border">{bus.DriverName}</td>
                                        <td className="px-2 py-1 border">
                                            {new Date(bus.ArrivalTime).toLocaleTimeString("en-EG", {
                                                hour: "2-digit", minute: "2-digit", hour12: false,
                                            })}
                                        </td>
                                        <td className="px-2 py-1 border">{bus.Duration}</td>
                                    </tr>
                                ))}
                        </tbody>

                    </table>
                </div>
            </div>
        </div>
    );
}

// Helper: Choose circle color
function getCircleColor(stepId, bt) {
    let bgColor = "bg-green-600";
    if (stepId === 0) {
        // انكشاف الخط
        if (bt.BusesCount === 0) {
            bgColor = "bg-red-600";
        }
        else if (bt.BusesCount > bt.MaxAllowed) {
            bgColor = "bg-green-600";
        }
        else if (bt.BusesCount <= bt.MaxAllowed) {
            bgColor = "bg-orange-500";
        }
    }
    else if (stepId === 6) {
        // القاعة للتحميل
        if (bt.BusesCount === 0) {
            bgColor = "bg-red-600";
        }
        else if (bt.BusesCount > bt.MaxAllowed) {
            bgColor = "bg-green-600";
        }
        else if (bt.BusesCount <= bt.MaxAllowed) {
            bgColor = "bg-orange-500";
        }
    }
    else if (stepId === 7) {
        // انطلاق من القاعة
        if (bt.BusesCount === 0) {
            bgColor = "bg-green-600";
        }
        else if (bt.BusesCount > bt.MaxAllowed) {
            bgColor = "bg-green-600";
        }
        else if (bt.BusesCount <= bt.MaxAllowed) {
            bgColor = "bg-green-600";
        }
    }
    else if (stepId === 9) {
        // أمامي أردني 
        if (bt.BusesCount === 0) {
            bgColor = "bg-green-600";
        }
        else if (bt.BusesCount > bt.MaxAllowed) {
            bgColor = "bg-red-600";
        }
        else if (bt.BusesCount <= bt.MaxAllowed) {
            bgColor = "bg-orange-500";
        }
    }
    else if (stepId === 10) {
        // مدينة الحجاج 
        if (bt.BusesCount === 0) {
            bgColor = "bg-green-600";
        }
        else if (bt.BusesCount > bt.MaxAllowed) {
            bgColor = "bg-red-600";
        }
        else if (bt.BusesCount <= bt.MaxAllowed) {
            bgColor = "bg-orange-500";
        }
    }
    else if (stepId === 11) {
        // القاعة الأردنية 
        if (bt.BusesCount === 0) {
            bgColor = "bg-green-600";
        }
        else if (bt.BusesCount > bt.MaxAllowed) {
            bgColor = "bg-red-600";
        }
        else if (bt.BusesCount <= bt.MaxAllowed) {
            bgColor = "bg-orange-500";
        }
    }
    else if (stepId === 12) {
        // جيمل اياب 
        if (bt.BusesCount === 0) {
            bgColor = "bg-green-600";
        }
        else if (bt.BusesCount > bt.MaxAllowed) {
            bgColor = "bg-red-600";
        }
        else if (bt.BusesCount <= bt.MaxAllowed) {
            bgColor = "bg-orange-500";
        }
    }
    else if (stepId === 13) {
        // الجورة
        if (bt.BusesCount === 0) {
            bgColor = "bg-red-600";
        }
        else if (bt.BusesCount > bt.MaxAllowed) {
            bgColor = "bg-green-600";
        }
        else if (bt.BusesCount <= bt.MaxAllowed) {
            bgColor = "bg-orange-500";
        }
    }
    else if (stepId === 15) {
        // قاعة 18
        if (bt.BusesCount === 0) {
            bgColor = "bg-green-600";
        }
        else if (bt.BusesCount > bt.MaxAllowed) {
            bgColor = "bg-red-600";
        }
        else if (bt.BusesCount <= bt.MaxAllowed) {
            bgColor = "bg-orange-500";
        }
    }
    else if (stepId === 16 || stepId === 17) {
        // انطلاق القاعة الأردنية || انطلاق مدينة الحجاج
        if (bt.BusesCount === 0) {
            bgColor = "bg-green-600";
        }
        else if (bt.BusesCount > bt.MaxAllowed) {
            bgColor = "bg-orange-500";
        }
        else if (bt.BusesCount <= bt.MaxAllowed) {
            bgColor = "bg-orange-500";
        }
    }
    else if (stepId === 17) {
        // بدء تحميل عرب
        if (bt.BusesCount === 0) {
            bgColor = "bg-red-600";
        }
        else {
            bgColor = "bg-green-600";
        }
    }
    else if (stepId === 22) {
        // استلام سلة
        if (bt.BusesCount === 0) {
            bgColor = "bg-green-600";
        }
        else {
            bgColor = "bg-green-600";
        }
    }
    else if (stepId === 23) {
        // انطلاق سلة
        if (bt.BusesCount === 0) {
            bgColor = "bg-green-600";
        }
        else if (bt.BusesCount > bt.MaxAllowed) {
            bgColor = "bg-red-600";
        }
        else if (bt.BusesCount <= bt.MaxAllowed) {
            bgColor = "bg-orange-500";
        }
    }
    else if (stepId === 24) {
        // مكان التنزيل
        if (bt.BusesCount === 0) {
            bgColor = "bg-green-600";
        }
        else if (bt.BusesCount > bt.MaxAllowed) {
            bgColor = "bg-red-600";
        }
        else if (bt.BusesCount <= bt.MaxAllowed) {
            bgColor = "bg-orange-500";
        }
    }
    else if (stepId === 25) {
        // بدء التنزيل
        if (bt.BusesCount === 0) {
            bgColor = "bg-green-600";
        }
        else if (bt.BusesCount > bt.MaxAllowed) {
            bgColor = "bg-red-600";
        }
        else if (bt.BusesCount <= bt.MaxAllowed) {
            bgColor = "bg-orange-500";
        }
    }
    else if (stepId === 26) {
        // انطلاق مكان التنزيل
        if (bt.BusesCount === 0) {
            bgColor = "bg-green-600";
        }
        else if (bt.BusesCount > bt.MaxAllowed) {
            bgColor = "bg-red-600";
        }
        else if (bt.BusesCount <= bt.MaxAllowed) {
            bgColor = "bg-orange-500";
        }
    }
    else if (stepId === 29) {
        // قاعة 15
        if (bt.BusesCount === 0) {
            bgColor = "bg-green-600";
        }
        else if (bt.BusesCount > bt.MaxAllowed) {
            bgColor = "bg-red-600";
        }
        else if (bt.BusesCount <= bt.MaxAllowed) {
            bgColor = "bg-orange-500";
        }
    }
    else if (stepId === 30) {
        // انطلاق من الجورة
        if (bt.BusesCount === 0) {
            bgColor = "bg-green-600";
        }
        else if (bt.BusesCount > bt.MaxAllowed) {
            bgColor = "bg-green-600";
        }
        else if (bt.BusesCount <= bt.MaxAllowed) {
            bgColor = "bg-green-600";
        }
    }
    else if (stepId === 32 || stepId === 42 || stepId === 43 || stepId === 47) {
        // محطة التحميل || محطة التحميل سياحة || محطة التحميل عمرة || محطة تحميل 2
        if (bt.BusesCount === 0) {
            bgColor = "bg-red-600";
        }
        else if (bt.BusesCount > bt.MaxAllowed) {
            bgColor = "bg-green-600";
        }
        else if (bt.BusesCount <= bt.MaxAllowed) {
            bgColor = "bg-orange-500";
        }
    }
    else if (stepId === 40) {
        // محطة الشاحنات
        if (bt.BusesCount > 0) {
            bgColor = "bg-green-600";
        }
    }
    else if (stepId === 48) {
        // عطل طارئ
        if (bt.BusesCount > 0) {
            bgColor = "bg-red-600";
        }
    }
    else if (stepId === 52 || stepId === 53 || stepId === 54) {
        // انطلاق القاعة الأردنية / حقائب القدس || انهاء تنزيل حقائب القدس || انطلاق جيمل
        if (bt.BusesCount > 0) {
            bgColor = "bg-green-600";
        }
    }
    else if (stepId === 33 || stepId === 34 || stepId === 35 || stepId === 36 || stepId === 45 || stepId === 56) {
        // بدء تحميل عرب 2 || بدء تحميل عمرة/حج || بدء تحميل سياحة 
        // تشييك وجمع تذاكر || بدء تحميل حالات || بدء تحميل عرب 2 طارئ
        if (bt.BusesCount === 0) {
            bgColor = "bg-orange-500";
        }
        else {
            bgColor = "bg-green-600";
        }
    }
    else if (stepId === 60) {
        if (bt.BusesCount === 0) {
            bgColor = "bg-green-600";
        }
        else if (bt.BusesCount > bt.MaxAllowed) {
            bgColor = "bg-red-600";
        }
        else if (bt.BusesCount <= bt.MaxAllowed) {
            bgColor = "bg-green-600";
        }
    }
    else if (stepId === 66 || stepId === 67 || stepId === 68) {
        // تعبئة سولار || بدء تعبئة سولار || انهاء تعبئة سولار
        if (bt.BusesCount === 0) {
            bgColor = "bg-green-600";
        }
        else if (bt.BusesCount > bt.MaxAllowed) {
            bgColor = "bg-orange-500";
        }
        else if (bt.BusesCount <= bt.MaxAllowed) {
            bgColor = "bg-orange-500";
        }
    }
    else {
        if (bt.BusesCount === 0) {
            bgColor = "bg-red-600";
        } else if (bt.BusesCount > bt.MaxAllowed) {
            bgColor = "bg-red-600";
        } else if (bt.BusesCount <= bt.MaxAllowed) {
            bgColor = "bg-green-600";
        }

        if (bt.BusesCount === 0 && stepId !== 6) {
            bgColor = "bg-orange-500";
        }
        if (bt.BusesCount === 0 && stepId === 12) {
            bgColor = "bg-green-600";
        }

        if (bt.BusesCount === 0) {
            bgColor = "bg-red-600";
        } else if (bt.BusesCount > bt.MaxAllowed) {
            bgColor = "bg-red-600";
        } else if (bt.BusesCount <= bt.MaxAllowed) {
            bgColor = "bg-green-600";
        }

        if (bt.BusesCount.BusesCount === 0 && stepId !== 6) {
            bgColor = "bg-orange-500";
        }
    }
    return bgColor;

    // if (bt.BusesCount === 0) return 'bg-red-600';
    // if (bt.BusesCount <= bt.MaxAllowed) return 'bg-orange-500';
    // return 'bg-green-600';
}
function getCircleSize(busTypeId, count) {
    let size = "w-48 h-48";
    if (busTypeId === 1) {
        size = "w-48 h-48";
    }
    else {
        if (count <= 8) {
            size = "w-56 h-56";
        }
        else if (count <= 10) {
            size = "w-48 h-48";
        }
    }
    return size;
}
